/**
 * Fiko-Analiz Sistemi
 * CATIA benzeri analiz araçları:
 * - Draft Angle Analysis (Ters açı analizi)
 * - Undercut Detection (CNC undercut tespiti)
 * - Wall Thickness Analysis (Duvardan duvara kalınlık analizi - CATIA Thickness Check benzeri)
 */

import * as THREE from 'three';
import { FikoAnalizComponent } from './fiko-analiz.component';
import { DraftAngleComponent } from './draft-angle/draft-angle.component';
import { UndercutComponent } from './undercut/undercut.component';
import { WallThicknessComponent } from './wall-thickness/wall-thickness.component';

// Main Classes
export { FikoAnalizComponent } from './fiko-analiz.component';
export { DraftAngleComponent } from './draft-angle/draft-angle.component';
export { UndercutComponent } from './undercut/undercut.component';
export { WallThicknessComponent } from './wall-thickness/wall-thickness.component';

// Types and Interfaces
export interface FikoAnalizAyarlar {
  moldYonu: THREE.Vector3;
  aciEsinigi: number;
  gostergeRenkleri: {
    iyi: string;
    kritik: string;
    notr: string;
  };
}

// Standalone Component Declarations
export const FIKO_ANALIZ_COMPONENTS = [
  FikoAnalizComponent,
  DraftAngleComponent,
  UndercutComponent,
  WallThicknessComponent,
];

// Version Information
export const FIKO_ANALIZ_VERSION = '2.2.0';
export const FIKO_ANALIZ_INFO = {
  name: 'Fiko-Analiz Sistemi',
  version: FIKO_ANALIZ_VERSION,
  description: 'CATIA benzeri ters açı analizi, CNC undercut tespiti ve duvar kalınlık analizi',
  features: [
    'GPU accelerated shader-based analiz',
    'Draft angle analizi',
    'Undercut tespiti',
    'Duvar kalınlık analizi (CATIA Thickness Check benzeri)',
    'Multi-pass GPU ray marching (6 yön)',
    'Real-time gradient color mapping',
    'Kritik bölge vurgulama',
    'CATIA 3 katmanlı mantık: Topolojik bölge + Ray marching + Thickness validation',
    'Gerçek zamanlı açı ve kalınlık hesaplama',
    'GPU depth texture ile karşı yüzey tespiti',
    'Özelleştirilebilir eşik değerleri',
    'Interaktif kontrol arayüzü',
    'Native performans optimizasyonu'
  ]
};

// Utility Functions
export const FikoAnalizUtils = {
  /**
   * Varsayılan analiz ayarları oluştur
   */
  createDefaultAyarlar(): FikoAnalizAyarlar {
    return {
      moldYonu: new THREE.Vector3(0, 0, 1),
      aciEsinigi: 3.0,
      gostergeRenkleri: {
        iyi: '#00ff33',
        kritik: '#ff3333',
        notr: '#ffff00'
      }
    };
  },

  /**
   * Dereceyi radyana çevir
   */
  dereceToRadyan(derece: number): number {
    return derece * Math.PI / 180;
  },

  /**
   * Radyanı dereceye çevir
   */
  radyanToDerece(radyan: number): number {
    return radyan * 180 / Math.PI;
  },

  /**
   * İki vektör arasındaki açıyı hesapla (radyan)
   */
  aciHesapla(v1: THREE.Vector3, v2: THREE.Vector3): number {
    const normalizedV1 = v1.clone().normalize();
    const normalizedV2 = v2.clone().normalize();
    const dotProduct = Math.max(-1, Math.min(1, normalizedV1.dot(normalizedV2)));
    return Math.acos(dotProduct);
  },

  /**
   * Açının durumunu belirle
   */
  aciDurumuBelirle(aci: number, esik: number): 'iyi' | 'kritik' | 'notr' {
    const aciDerece = aci * 180 / Math.PI;

    if (aciDerece < esik) {
      return 'kritik';
    } else if (aciDerece < 60) {
      return 'notr';
    } else {
      return 'iyi';
    }
  },

  /**
   * Shader renk kodu oluştur
   */
  shaderRenkOlustur(renk: string): THREE.Vector3 {
    const renkHex = parseInt(renk.replace('#', ''), 16);
    return new THREE.Vector3(
      ((renkHex >> 16) & 255) / 255,
      ((renkHex >> 8) & 255) / 255,
      (renkHex & 255) / 255
    );
  }
};

// THREE.js dependency (export for external use)
export * from 'three';
